import React, { useState } from 'react';
import { useBadgeList } from '@/hooks/queries/badge/useList';
import { useBadgeCreate, useBadgeUpdate, useBadgeDelete } from '@/hooks/mutations/badge';
import { CourseBadge, CourseBadgeInput } from '@/types/courseBadges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Award } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const BadgesPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<CourseBadge | null>(null);

  const [formData, setFormData] = useState<CourseBadgeInput>({
    icon: '',
    translations: [
      { locale: 'uz', title: '', description: '' },
      { locale: 'ru', title: '', description: '' },
      { locale: 'en', title: '', description: '' },
    ],
  });

  const { data: badgesData, isLoading, refetch } = useBadgeList({ pageNumber: page, pageSize: 50 });
  const { mutate: createBadge, isPending: isCreating } = useBadgeCreate();
  const { mutate: updateBadge, isPending: isUpdating } = useBadgeUpdate();
  const { mutate: deleteBadge } = useBadgeDelete();

  const handleOpenCreate = () => {
    setSelectedBadge(null);
    setFormData({
      icon: '',
      translations: [
        { locale: 'uz', title: '', description: '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (badge: CourseBadge) => {
    setSelectedBadge(badge);
    setFormData({
      icon: badge.icon || '',
      translations: [
        { locale: 'uz', title: badge.title || '', description: badge.description || '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      icon: formData.icon,
      translations: formData.translations,
    };

    if (selectedBadge) {
      updateBadge(
        { id: selectedBadge.id, data: payload },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Nishon yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Nishon yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createBadge(payload, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi nishon yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Nishon yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedBadge) return;
    deleteBadge(selectedBadge.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Nishon o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedBadge(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Nishon o'chirilmadi", variant: 'destructive' }),
    });
  };

  const badgesList: CourseBadge[] = badgesData?.data?.data || badgesData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Nishonlari (Badges)</h1>
          <p className="text-sm text-slate-500 mt-1">Skill kurslarini tugatgan talabalarga beriluvchi nishonlar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi nishon
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : badgesList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Award className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha nishonlar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Ikonka</th>
                  <th className="px-4 py-3.5">Nishon Nomi</th>
                  <th className="px-4 py-3.5">Tavsif</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {badgesList.map((badge) => (
                  <tr key={badge.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {badge.icon ? (
                        <img src={getMediaUrl(badge.icon)} alt={badge.title} className="size-10 object-contain rounded-lg border border-slate-100 p-1" />
                      ) : (
                        <div className="size-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                          <Award className="size-5" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{badge.title || '—'}</td>
                    <td className="px-4 py-3 max-w-md truncate text-slate-500">{badge.description || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(badge)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedBadge(badge);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={badgesData?.meta?.pagination ?? badgesData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedBadge ? 'Nishonni tahrirlash' : 'Yangi nishon yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-4">
              <FileUploadInput
                label="Ikonka (Rasm)"
                value={formData.icon}
                onChange={(url) => setFormData({ ...formData, icon: url })}
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { title: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Nishon nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="JavaScript Master"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="Kursni a'lo baholarga tugatganligi uchun..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Nishonni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu nishon butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default BadgesPage;
